export const MASTER_MEETING_ID = 'vasia-master-sunday-review';

const MASTER_REVIEW_GROUPS = [
  {
    title: '1. Access, ownership & baseline',
    purpose: 'Confirm that Vasia owns the accounts, recovery routes and data needed to run the business safely.',
    items: [
      'List every active VIP, Instagram, Reddit, X, streaming and backup account.',
      'Record the email and phone-number owner for every account.',
      'Confirm two-factor authentication and secure recovery codes wherever available.',
      'Record everyone with account access and remove access that is no longer required.',
      'Check whether any supplier controls an account that should belong to Vasia.',
      'Record current warnings, restrictions, bans and account-health concerns.',
      'Review each provider, the accounts they access, their responsibilities, monthly cost and notice position.',
      'Confirm Vasia retains ultimate ownership of every underlying account.',
      'Collect the latest revenue, cost, VIP subscriber, social follower and streaming baseline figures.'
    ]
  },
  {
    title: '2. OnlyFans VIP review',
    purpose: 'Review the one active VIP account as the central sales account.',
    items: [
      'Review monthly revenue, new subscribers, renewals, expiries and churn.',
      'Review subscription, PPV, tips and custom-content revenue.',
      'Review average revenue per fan and per new subscriber.',
      'Identify the highest-spending fans and compare revenue with previous months.',
      'Review profile picture, banner, bio and positioning.',
      'Review subscription price, discounts, promotions and renewal incentives.',
      'Review the welcome message, pinned posts and wall-post quality/frequency.',
      'Review PPV strategy, pricing and content-vault organisation.',
      'Review expired-fan reactivation and confirm the account communicates Vasia’s personality and value.'
    ]
  },
  {
    title: '3. Chatting & whales',
    purpose: 'Separate chatting performance from marketing and check relationship quality as well as sustainable revenue.',
    items: [
      'Review chatter hours, conversations, response time, fans contacted and response rate.',
      'Review PPVs sent, purchase/open rate, PPV revenue, tips, customs and upsells.',
      'Review renewal saves, total revenue, revenue per hour and revenue per conversation.',
      'Sample real conversations and confirm the chatter sounds like Vasia.',
      'Check continuity, natural conversation, flirting, relationship building and sales timing.',
      'Check PPV selection/pricing, objection handling, follow-up, grammar and response speed.',
      'Check for obvious AI behaviour or overly pushy scripted selling.',
      'Review each priority whale’s lifetime/recent spend, last interaction and preferences.',
      'Confirm the responsible chatter, flag churn risk and agree the next action for each priority whale.'
    ]
  },
  {
    title: '4. Media performance',
    purpose: 'Find which photos, videos and Reels create the best combination of attention, clicks, VIP subscribers and revenue.',
    items: [
      'Review platform, account, date, format, views/reach and engagement for important media.',
      'Review shares, saves, profile visits, follower growth and tracking-link clicks where available.',
      'Review VIP conversions and attributable revenue where measurable.',
      'Identify winning hooks, outfits, locations, camera angles and video lengths.',
      'Identify winning captions, on-screen text, audio, posting times and personality themes.',
      'Separate high-view/low-conversion content from lower-view/high-conversion content.',
      'Choose content to repeat with controlled variations.',
      'Choose content to stop or redesign.',
      'Confirm the media library is tagged by look, theme, platform, date used and performance.',
      'Confirm each asset shows where it has already been used.'
    ]
  },
  {
    title: '5. Social media: Instagram, Reddit & X',
    purpose: 'Review every account separately and decide which channels need more effort, less effort or a change of approach.',
    items: [
      'Instagram: review follower growth, reach, Reels, Stories, engagement, profile visits and clicks.',
      'Instagram: review VIP conversion, warnings, flagged content, reach drops and audience quality.',
      'Instagram: identify what caused successful spikes and compare views with clicks/conversion.',
      'Reddit: review every account’s karma, age, status, subreddits and posting frequency.',
      'Reddit: review scores/comments, removals, bans, shadowban signs and duplicate-content risk.',
      'Reddit: identify converting subreddits and review rules, verification and new-account restrictions.',
      'Reddit: review Vasia’s own subreddit plan, safe karma building and provider value.',
      'X: review followers, impressions, engagement, profile visits, clicks and VIP conversion.',
      'X: review frequency, best media/hooks, replies, quote-post strategy and account health.',
      'Give Instagram, Reddit and X a clear decision: increase, maintain, change or reduce effort.'
    ]
  },
  {
    title: '6. Streaming',
    purpose: 'Treat streaming as its own acquisition, revenue and reusable-content channel.',
    items: [
      'Review platform, account, date/time, length, peak viewers and average viewers for each stream.',
      'Review new followers, engagement, profile visits and tracking-link clicks.',
      'Review attributable VIP subscriptions and direct stream revenue/gifts.',
      'Identify the best topics, outfits, games and activities.',
      'Mark strong moments for short-form clips and Stories.',
      'Choose suitable Instagram, X and Reddit versions and store them in the media library.',
      'Compare stream-derived clips with separately filmed content.',
      'Agree the best days/times, formats, realistic frequency and call-to-action.',
      'Decide whether streaming is mainly for followers, VIP clicks, direct revenue or content creation.'
    ]
  },
  {
    title: '7. Tracking & attribution',
    purpose: 'Connect each important source to clicks, VIP subscribers and revenue wherever the data allows it.',
    items: [
      'Confirm separate tracking for every Instagram, Reddit and X account.',
      'Use separate tracking for important subreddits, streaming, collaborations and paid promotion.',
      'Confirm each provider and important campaign/test has a separate tracking source.',
      'Review reach/impressions, profile visits, clicks, VIP subscribers and conversion rate.',
      'Review fan spend/revenue where attributable.',
      'Compare provider/channel cost with attributable revenue.',
      'Separate vanity metrics from activity that produces paying fans.',
      'Report the chain: reach/impressions → profile visits → clicks → VIP subscribers → revenue.',
      'Keep every attribution limitation visible rather than presenting guesses as facts.'
    ]
  },
  {
    title: '8. Marketing strategy & providers',
    purpose: 'Measure every provider and growth test, then decide what should be kept, changed, expanded or stopped.',
    items: [
      'Review each provider’s monthly cost, promised work and delivered work.',
      'Review activity, reach/followers, clicks, VIP subscribers and attributable revenue.',
      'Review bans/removals, communication, reporting, reliability and return on cost.',
      'Decide for each provider: keep, improve, renegotiate, replace or stop.',
      'Review active personality, comedy, cosplay, gaming/anime and girlfriend-style tests.',
      'Review lifestyle, POV, trends, storytelling, reaction, meme and collaboration tests where used.',
      'For every test, confirm the hypothesis, period, traffic, VIP subscriptions and revenue.',
      'Decide for every test: scale, repeat, change or stop.',
      'Review comparable creators’ hooks, repeatable formats, frequency, positioning and calls-to-action.',
      'Adapt useful formats to Vasia rather than copying blindly.'
    ]
  },
  {
    title: '9. Content planning & automation',
    purpose: 'Make filming, editing and repurposing predictable so Vasia does not start from a blank page.',
    items: [
      'Review the pipeline from idea/reference through script, shot list and filming instructions.',
      'Review filming, raw-footage upload, editing, approval, scheduling and performance capture.',
      'Confirm performance results feed back into future ideas.',
      'Review reference-Reel analysis: hook, shot order, camera, movement, dialogue, text, audio and pacing.',
      'Confirm the output gives Vasia a genuinely simple filming list.',
      'Review automatic clip selection, cutting, captions, effects, crops and alternative versions.',
      'Confirm one filming session produces Instagram, Story, X, Reddit and promotional assets where useful.',
      'Store all outputs in the content library.',
      'Prioritise ideas that produced clicks/VIP subscribers, create variations of winners and flag repeat underperformers.',
      'Produce the next weekly filming list from performance data.'
    ]
  },
  {
    title: '10. Dashboard, alerts & review routine',
    purpose: 'Bring the operation together, catch problems early and leave with a focused next step.',
    items: [
      'Review revenue, net profit, subscribers, renewals/churn, fan value and marketing return.',
      'Review social traffic, tracking clicks, VIP conversion, chatting and streaming performance.',
      'Review account notes, whale notes, campaign notes, pricing tests and agreed actions.',
      'Review alerts for reach drops, restrictions, response-time issues and falling conversion/retention.',
      'Review alerts for breakout content, strong traffic sources, priority whales and provider underperformance.',
      'Confirm each dashboard screen stays focused on one job and clearly shows its next action.',
      'Record decisions and unresolved questions in the meeting notes.',
      'Write the topics that must carry into next Sunday’s agenda.',
      'Choose no more than three priority actions for the next seven days.'
    ]
  }
];

export const WEEKLY_REVIEW_GROUPS = [
  {
    title: 'Weekly performance review',
    purpose: 'A short operating review. Focus on changes, problems and decisions rather than repeating the full audit.',
    items: [
      'VIP revenue, new subscribers, renewals and churn.',
      'Chatting response speed, sales performance and any quality issue.',
      'Priority whales needing attention or at risk.',
      'Instagram strongest and weakest content.',
      'Reddit account health, strongest subreddits and removals/bans.',
      'X meaningful growth, clicks or conversion.',
      'Streaming results and clips that should be reused.',
      'Top three pieces of media and why they worked.',
      'Tracking sources that produced clicks or subscribers.',
      'Provider missed work or underperformance.',
      'Account warnings, access changes or risks.',
      'Next week’s filming/content list.',
      'The new test being run.',
      'Choose no more than three priority actions for the next seven days.'
    ]
  }
];

export const MONTHLY_REVIEW_GROUP = {
  title: 'First-Sunday monthly layer',
  purpose: 'Add the deeper monthly decisions to the normal Sunday review on the first Sunday of each month.',
  items: [
    'Review profitability, costs and revenue mix.',
    'Review VIP acquisition, retention and average fan value.',
    'Review chatting quality, whale retention and losses.',
    'Review Instagram, Reddit, X and streaming contribution.',
    'Review content patterns, attribution quality and provider return on cost.',
    'Review test results, competitor observations and account safety.',
    'Choose what to scale, stop and automate next month.'
  ]
};

export const MASTER_MEETING_TEMPLATE = {
  id: MASTER_MEETING_ID,
  title: 'Vasia Master Review & Growth Meeting',
  frequency: 'Every Sunday · 11:00 Norway time',
  purpose: 'One meeting containing the full master review. Finish with decisions, notes and no more than three priority actions.',
  groups: MASTER_REVIEW_GROUPS
};

export const MEETING_TEMPLATES = [MASTER_MEETING_TEMPLATE];

export const buildRecurringMeetingGroups = ({ carryForward = [], includeMonthly = false } = {}) => {
  const groups = carryForward.length
    ? [{
      title: 'Carry-forward from the previous meeting',
      purpose: 'Resolve or deliberately reschedule the items raised by the last meeting and its notes.',
      items: carryForward
    }]
    : [];
  groups.push(...WEEKLY_REVIEW_GROUPS.map((group) => ({ ...group, items: [...group.items] })));
  if (includeMonthly) groups.push({ ...MONTHLY_REVIEW_GROUP, items: [...MONTHLY_REVIEW_GROUP.items] });
  return groups;
};

export const countTemplateItems = (template) =>
  (template?.groups || []).reduce((total, group) => total + group.items.length, 0);

export const getChecklistKey = (groupIndex, itemIndex) => `${groupIndex}:${itemIndex}`;
